import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { DaysRangeSliderComponent } from '../days-range-slider/days-range-slider.component';
import {
  AdminUsersSummaryDataQueryHandler,
  AdminUsersSummaryQueryResult,
  DailyRegistrationQueryResult,
  VersionStatQueryResult
} from '../../application/handlers';

enum RegistrationsViewMode {
  BY_DAY = 'byDay',
  BY_REGION = 'byRegion'
}

@Component({
  selector: 'lib-users-stats',
  templateUrl: './users-stats.component.html',
  styleUrls: ['./users-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DaysRangeSliderComponent, TitileNavbarComponent, PageLayoutComponent],
  providers: [AdminUsersSummaryDataQueryHandler]
})
export class UsersStatsComponent {
  private readonly adminUsersSummaryDataQueryHandler: AdminUsersSummaryDataQueryHandler = inject(
    AdminUsersSummaryDataQueryHandler
  );

  readonly RegistrationsViewMode: typeof RegistrationsViewMode = RegistrationsViewMode;
  readonly statsDays: WritableSignal<number> = signal<number>(7);
  readonly registrationsOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly platformsOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly regionsOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly expandedDate: WritableSignal<string | null> = signal<string | null>(null);
  readonly registrationsViewMode: WritableSignal<RegistrationsViewMode> = signal<RegistrationsViewMode>(
    RegistrationsViewMode.BY_DAY
  );
  readonly expandedRegion: WritableSignal<string | null> = signal<string | null>(null);

  readonly summary: Signal<AdminUsersSummaryQueryResult | undefined> = toSignal(
    toObservable(this.statsDays).pipe(switchMap((days: number) => this.adminUsersSummaryDataQueryHandler.query(days)))
  );
  readonly versions: Signal<VersionStatQueryResult[]> = computed(
    () => this.summary()?.versionStats?.slice(0, 10) ?? []
  );
  readonly registrationsTotal: Signal<number> = computed(
    () =>
      this.summary()?.dailyRegistrations?.reduce(
        (sum: number, reg: DailyRegistrationQueryResult) => sum + reg.count,
        0
      ) ?? 0
  );

  setPeriod(period: number): void {
    this.statsDays.set(period);
  }

  toggleRegistrations(): void {
    this.registrationsOpen.update((v: boolean) => !v);
  }

  togglePlatforms(): void {
    this.platformsOpen.update((v: boolean) => !v);
  }

  toggleRegions(): void {
    this.regionsOpen.update((v: boolean) => !v);
  }

  toggleDate(rawDate: string): void {
    this.expandedDate.update((current: string | null) => (current === rawDate ? null : rawDate));
  }

  setRegistrationsViewMode(mode: RegistrationsViewMode): void {
    this.registrationsViewMode.set(mode);
    this.expandedDate.set(null);
    this.expandedRegion.set(null);
  }

  toggleRegion(regionName: string): void {
    this.expandedRegion.update((current: string | null) => (current === regionName ? null : regionName));
  }
}
